/**
 * Interest Rate History Component
 *
 * Displays and manages the history of interest rates for a bank account.
 * Allows adding new rates (which automatically closes the previous one)
 * and editing/deleting existing rates.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { Button, Input, Modal } from './ui';
import type { AccountInterestRate, CreateInterestRateRequest } from '@budgira/shared';

interface InterestRateHistoryProps {
  /** Account ID to manage rates for */
  accountId: string;
  /** Called when rates change (to refresh parent data) */
  onRatesChange?: () => void;
  /** Whether the component is disabled (e.g., during form submission) */
  disabled?: boolean;
}

interface RateFormData {
  rate: string;
  startDate: string;
}

export const InterestRateHistory = ({
  accountId,
  onRatesChange,
  disabled = false,
}: InterestRateHistoryProps) => {
  const { t } = useTranslation();
  const [rates, setRates] = useState<AccountInterestRate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RateFormData>({ rate: '', startDate: '' });
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<AccountInterestRate | null>(null);

  // Fetch rates from API
  const fetchRates = useCallback(async () => {
    if (!accountId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get<{ success: boolean; data: AccountInterestRate[] }>(
        `/accounts/${accountId}/interest-rates`
      );
      if (response.data.success) {
        setRates(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch interest rates:', err);
      setError(t('accounts.interestRates.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [accountId, t]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Handle adding a new rate
  const handleAddRate = async () => {
    if (!formData.rate || !formData.startDate) {
      setError(t('accounts.interestRates.validation.required'));
      return;
    }

    const rateValue = parseFloat(formData.rate);
    if (isNaN(rateValue) || rateValue < 0) {
      setError(t('accounts.interestRates.validation.invalidRate'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const request: CreateInterestRateRequest = {
        rate: rateValue,
        startDate: formData.startDate,
      };

      await api.post(`/accounts/${accountId}/interest-rates`, request);
      
      setFormData({ rate: '', startDate: '' });
      setShowAddForm(false);
      await fetchRates();
      onRatesChange?.();
    } catch (err) {
      console.error('Failed to add interest rate:', err);
      setError(t('accounts.interestRates.addError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle updating an existing rate
  const handleUpdateRate = async (id: string) => {
    if (!formData.rate || !formData.startDate) {
      setError(t('accounts.interestRates.validation.required'));
      return;
    }

    const rateValue = parseFloat(formData.rate);
    if (isNaN(rateValue) || rateValue < 0) {
      setError(t('accounts.interestRates.validation.invalidRate'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await api.put(`/accounts/${accountId}/interest-rates/${id}`, {
        rate: rateValue,
        startDate: formData.startDate,
      });
      
      setFormData({ rate: '', startDate: '' });
      setEditingId(null);
      await fetchRates();
      onRatesChange?.();
    } catch (err) {
      console.error('Failed to update interest rate:', err);
      setError(t('accounts.interestRates.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle deleting a rate
  const handleDeleteClick = (rate: AccountInterestRate) => {
    setRateToDelete(rate);
    setShowDeleteModal(true);
  };

  const confirmDeleteRate = async () => {
    if (!rateToDelete) return;

    setIsSaving(true);
    setError(null);

    try {
      await api.delete(`/accounts/${accountId}/interest-rates/${rateToDelete.id}`);
      setShowDeleteModal(false);
      setRateToDelete(null);
      await fetchRates();
      onRatesChange?.();
    } catch (err) {
      console.error('Failed to delete interest rate:', err);
      setError(t('accounts.interestRates.deleteError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Start editing a rate
  const startEditing = (rate: AccountInterestRate) => {
    setEditingId(rate.id);
    setFormData({
      rate: rate.rate.toString(),
      startDate: rate.startDate,
    });
    setShowAddForm(false);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setFormData({ rate: '', startDate: '' });
    setError(null);
  };

  // Cancel adding
  const cancelAdding = () => {
    setShowAddForm(false);
    setFormData({ rate: '', startDate: '' });
    setError(null);
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  // Format rate for display
  const formatRate = (rate: number) => {
    return `${rate.toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('accounts.interestRates.title')}
        </h4>
        {!showAddForm && !editingId && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setShowAddForm(true);
              setFormData({ rate: '', startDate: new Date().toISOString().split('T')[0] || '' });
            }}
            disabled={disabled}
          >
            {t('accounts.interestRates.addRate')}
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
          {error}
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {t('accounts.interestRates.rate')}
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                placeholder="3.00"
                disabled={isSaving}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {t('accounts.interestRates.startDate')}
              </label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={cancelAdding}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAddRate}
              disabled={isSaving}
            >
              {isSaving ? t('common.saving') : t('common.add')}
            </Button>
          </div>
        </div>
      )}

      {/* Rates list */}
      {rates.length === 0 && !showAddForm ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          {t('accounts.interestRates.noRates')}
        </div>
      ) : (
        <div className="space-y-2">
          {rates.map((rate) => (
            <div
              key={rate.id}
              className={`p-3 rounded-lg border ${
                rate.endDate === null
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              {editingId === rate.id ? (
                // Edit form
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {t('accounts.interestRates.rate')}
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.rate}
                        onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                        disabled={isSaving}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {t('accounts.interestRates.startDate')}
                      </label>
                      <Input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={cancelEditing}
                      disabled={isSaving}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleUpdateRate(rate.id)}
                      disabled={isSaving}
                    >
                      {isSaving ? t('common.saving') : t('common.save')}
                    </Button>
                  </div>
                </div>
              ) : (
                // Display mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatRate(rate.rate)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('accounts.interestRates.from')} {formatDate(rate.startDate)}
                      {rate.endDate && (
                        <>
                          {' '}{t('accounts.interestRates.to')} {formatDate(rate.endDate)}
                        </>
                      )}
                    </div>
                    {rate.endDate === null && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300 rounded">
                        {t('accounts.interestRates.current')}
                      </span>
                    )}
                  </div>
                  {!disabled && !showAddForm && !editingId && (
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing(rate)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                        onClick={() => handleDeleteClick(rate)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('accounts.interestRates.helpText')}
      </p>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRateToDelete(null);
        }}
        title={t('accounts.interestRates.deleteModal.title', 'Supprimer le taux')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setRateToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteRate}
              disabled={isSaving}
            >
              {isSaving ? t('common.deleting', 'Suppression...') : t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 dark:text-slate-300">
          {t('accounts.interestRates.deleteModal.message', 'Êtes-vous sûr de vouloir supprimer le taux de {{rate}}% du {{date}} ? Cette action est irréversible.', {
            rate: rateToDelete?.rate.toFixed(2),
            date: rateToDelete ? new Date(rateToDelete.startDate).toLocaleDateString() : '',
          })}
        </p>
      </Modal>
    </div>
  );
};

export default InterestRateHistory;
